import { useEffect, useMemo, useState } from 'react';
import { ReactFlowProvider } from '@xyflow/react';
import { AlertTriangle, ArrowLeft, Check, CloudOff, Loader2, Sparkles, X } from 'lucide-react';
import { Permissao, TipoEventoQuadro, type LicitacaoDto } from '@nexital/shared';
import { criarStoreLicitacao } from '../../application/licitacao-store';
import { criarStoreNotas } from '../../application/notas-store';
import { useColaboracao, useDependencias, useQuadro } from '../../application/dependencias';
import type { AlvoLicitacao } from '../../application/portas';
import type { Salvamento } from '../../application/quadro-store';
import { ProvedorNotas } from '../notas/contexto-notas';
import { ProvedorLicitacao, useLicitacao } from './contexto';
import { PainelEdital } from './PainelEdital';
import { QuadroDaLicitacao } from './QuadroDaLicitacao';
import { BuscaGeral } from '../shell/BuscaGeral';

const SALVAMENTO: Record<Salvamento, { texto: string; classe: string }> = {
  salvo: { texto: 'Tudo salvo', classe: 'pilula--sucesso' },
  salvando: { texto: 'Salvando…', classe: 'pilula--informacao' },
  erro: { texto: 'Erro ao salvar', classe: 'pilula--urgencia' },
  conflito: { texto: 'Alterado por outra pessoa — recarregado', classe: 'pilula--aguardando' },
  offline: { texto: 'Offline', classe: 'pilula--aguardando' },
};

function IndicadorSalvamento() {
  const salvamento = useLicitacao((s) => s.salvamento);
  const { texto, classe } = SALVAMENTO[salvamento];
  const Icone = { salvo: Check, salvando: Loader2, erro: AlertTriangle, conflito: AlertTriangle, offline: CloudOff }[salvamento];
  return (
    <span className={`pilula ${classe}`} role="status">
      <Icone size={14} aria-hidden className={salvamento === 'salvando' ? 'girando' : undefined} /> {texto}
    </span>
  );
}

function Aviso() {
  const aviso = useLicitacao((s) => s.aviso);
  const dispensar = useLicitacao((s) => s.dispensarAviso);
  if (!aviso) return null;
  return (
    <div className="licitacao__aviso" role="alert">
      <AlertTriangle size={16} aria-hidden /> {aviso}
      <button type="button" className="botao-icone" onClick={dispensar} aria-label="Fechar aviso"><X size={14} /></button>
    </div>
  );
}

/** Objeto, etapa atual, número e órgão (referência B). */
function Titulo({ dados }: { dados: LicitacaoDto }) {
  const { processo } = dados;
  const estado = dados.etapas.find((e) => e.uuid === processo.estadoUuid);
  return (
    <div className="licitacao__titulo">
      <h1>{processo.objeto}</h1>
      {estado && <span className="pilula pilula--lilas">{estado.nome}</span>}
      <p>{processo.numero}{processo.orgao && ` • ${processo.orgao.nome}`}</p>
    </div>
  );
}

function Conteudo({ fechar, nomeQuadro, alvo }: { fechar: () => void; nomeQuadro: string; alvo: AlvoLicitacao }) {
  const dados = useLicitacao((s) => s.dados);
  const erro = useLicitacao((s) => s.erroCarga);
  const [edital, definirEdital] = useState(false);
  return (
    <section className="licitacao" role="dialog" aria-modal="true" aria-label={dados ? `${dados.processo.numero} — ${dados.processo.objeto}` : 'Licitação'}>
      <header className="licitacao__cabecalho">
        <nav className="licitacao__trilha" aria-label="Caminho">
          <button type="button" className="licitacao__voltar" onClick={fechar}>
            <ArrowLeft size={18} aria-hidden /> {nomeQuadro}
          </button>
          <span aria-hidden>/</span>
          <strong>{dados?.processo.numero ?? '…'}</strong>
          {dados && <IndicadorSalvamento />}
        </nav>
        <div className="licitacao__busca">
          <BuscaGeral processoUuid={alvo.processoUuid} />
        </div>
        <button type="button" className="licitacao__edital" aria-pressed={edital} onClick={() => definirEdital((v) => !v)}>
          <Sparkles size={16} aria-hidden /> Edital
        </button>
        <button type="button" className="botao-icone licitacao__fechar" onClick={fechar} aria-label="Fechar a licitação e voltar ao quadro">
          <X size={22} />
        </button>
        {dados && <Titulo dados={dados} />}
      </header>
      <div className="licitacao__quadro">
        {erro && <p className="licitacao__erro">Não foi possível abrir a licitação: {erro}</p>}
        {dados && (
          <ReactFlowProvider>
            <QuadroDaLicitacao />
          </ReactFlowProvider>
        )}
      </div>
      {edital && <PainelEdital alvo={alvo} fechar={() => definirEdital(false)} />}
      <Aviso />
    </section>
  );
}

/**
 * Licitação aberta sobre o quadro (RQ-LIC-001 a RQ-LIC-004), com link próprio (RQ-LIC-003). O quadro continua
 * montado por trás, com a câmera como estava (RQ-QDR-008). Esc fecha, se nenhuma caixa de diálogo estiver aberta.
 */
/**
 * Notas da licitação aberta: post-its e setas do quadro dela e notas de lista (RQ-NOT-002). Relê quando chega
 * evento de nota ou de seta (RQ-COL-001).
 */
function useNotasDaLicitacao(processoUuid: string) {
  const { apiNotas, sessao } = useDependencias();
  const membros = useQuadro((s) => s.dados?.membros);
  const permissoes = useQuadro((s) => s.dados?.permissoes);
  const notas = useMemo(() => criarStoreNotas(apiNotas, { quadroUuid: sessao.quadroUuid, processoUuid }), [apiNotas, sessao.quadroUuid, processoUuid]);
  useEffect(() => {
    void notas.getState().carregar();
  }, [notas]);
  useEffect(() => {
    if (membros && permissoes) notas.getState().configurar({ pessoasQuadro: membros, podeTornarPublica: permissoes.includes(Permissao.CriarNotasPublicas) });
  }, [notas, membros, permissoes]);
  const ultimoEvento = useColaboracao((s) => s.ultimoEvento);
  useEffect(() => {
    if (ultimoEvento?.tipo === TipoEventoQuadro.Nota || ultimoEvento?.tipo === TipoEventoQuadro.Conector) void notas.getState().carregar();
  }, [ultimoEvento, notas]);
  return notas;
}

export function TelaLicitacao({ processoUuid, fechar, nomeQuadro }: { processoUuid: string; fechar: () => void; nomeQuadro: string }) {
  const { apiLicitacao, sessao } = useDependencias();
  const alvo = useMemo(() => ({ quadroUuid: sessao.quadroUuid, processoUuid }), [sessao.quadroUuid, processoUuid]);
  const store = useMemo(() => criarStoreLicitacao(apiLicitacao, alvo), [apiLicitacao, alvo]);
  const notas = useNotasDaLicitacao(processoUuid);

  useEffect(() => {
    void store.getState().carregar();
  }, [store]);

  // Outra pessoa mexeu nesta licitação (ou no cartão dela): relê (RQ-COL-001).
  const ultimoEvento = useColaboracao((s) => s.ultimoEvento);
  useEffect(() => {
    if (ultimoEvento?.entidadeUuid === processoUuid) void store.getState().carregar();
  }, [ultimoEvento, processoUuid, store]);

  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !document.querySelector('dialog[open]')) fechar();
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [fechar]);

  return (
    <ProvedorLicitacao store={store}>
      <ProvedorNotas store={notas}>
        <div className="licitacao-fundo">
          <Conteudo fechar={fechar} nomeQuadro={nomeQuadro} alvo={alvo} />
        </div>
      </ProvedorNotas>
    </ProvedorLicitacao>
  );
}
